import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Compass,
  Home,
  LayoutGrid,
  Menu,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import CitySelect, { ALL_CITIES } from './CitySelect'
import PublicUserMenu from './PublicUserMenu'
import WhatsAppLink from './WhatsAppLink'
import { useOnExplore } from '@/hooks/useOnExplore'
import {
  CREATOR_PAGES_PATH,
  EXPLORE_PATH,
  PUBLIC_CONTAINER,
  whatsappHref,
} from './publicShell'
import type { CreatorAuthProp } from '@/types/creator'
import type { CityOption } from '@/types/publicSchool'

/** Logo sobre fundo claro; a variante branca é para quando o header for colorido. */
const LOGO = {
  default: '/bewave/logo.png',
  white: '/bewave/logo-white.png',
} as const

interface NavLink {
  label: string
  href: string
  icon: LucideIcon
  /**
   * Outros prefixos que também acendem este link. Sem uso hoje — ficou de
   * quando a página de cada negócio morava fora do prefixo do Explore.
   */
  alsoUnder?: string[]
}

/**
 * Qual link de página aparece depende do host — e, no Explore, de haver criador
 * logado.
 *
 * Este header é o mesmo nos dois sites — a landing comercial (bewave.surf) e o
 * Explore (explore.bewave.surf) — e depois da separação cada um é a raiz do
 * próprio domínio. Mostrar "Início" e "Explorar" juntos apontaria os dois para
 * `/`: dois rótulos, um destino, e o marcador de "página atual" acenderia nos
 * dois ao mesmo tempo.
 *
 * "Minhas páginas" é o terceiro caso: estava só dentro do menu do avatar, e no
 * Explore isso deixava a barra com um item só — "Explorar", que na home do
 * diretório aponta para a página em que a pessoa já está. Quem tem página
 * publicada volta para ela o tempo todo; esse caminho é de barra, não de menu
 * escondido atrás de um ícone.
 *
 * Cada link leva um ícone — a navegação do desktop e a gaveta do mobile lêem
 * daqui.
 */
const HOME_LINK: NavLink = { label: 'Início', href: '/', icon: Home }
const EXPLORE_LINK: NavLink = {
  label: 'Explorar',
  href: EXPLORE_PATH,
  icon: Compass,
}
const MY_PAGES_LINK: NavLink = {
  label: 'Minhas páginas',
  href: CREATOR_PAGES_PATH,
  icon: LayoutGrid,
}

/**
 * O convite para quem quer a BeWave na própria escola.
 *
 * Não é página, é conversa: cai no WhatsApp com o assunto já escrito. Fica fora de
 * `NAV_LINKS` de propósito — sublinhar como "página atual" um link que sai do site
 * seria mentira, e ele nunca fica ativo.
 *
 * Não aparece no Explore. Lá o header é do diretório: quem chega está procurando
 * negócio, e quem quer entrar no diretório tem convite próprio ("Criar sua
 * página"). Oferecer a plataforma de gestão no topo de toda página do Explore é
 * vender outro produto para quem não pediu.
 */
const WANT_BEWAVE = {
  label: 'Quero a BeWave',
  href: whatsappHref('Olá! Quero levar a BeWave para a minha escola.'),
} as const

/**
 * Qual link está aceso.
 *
 * A home casa exata (senão `/` acenderia em toda página, já que toda rota começa
 * com barra); as outras acendem também nas filhas, e nos prefixos que `alsoUnder`
 * declarar — a página de uma escola é `/schools/:slug`, e ali quem fica aceso é
 * "Explorar".
 */
function isActive(pathname: string, link: NavLink): boolean {
  if (link.href === '/') return pathname === '/'

  return [link.href, ...(link.alsoUnder ?? [])].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

/**
 * Aparência de um link de página.
 *
 * O sublinhado é o que diz onde você está, na cor `primary` da aplicação — a mesma
 * do botão "Entrar" ao lado e da sidebar do admin. A borda existe sempre,
 * transparente no link inativo: colorir uma borda que já ocupa espaço não mexe no
 * texto, enquanto acrescentá-la só no ativo empurraria os rótulos meio pixel.
 *
 * `w-fit` porque o menu do mobile empilha os links em blocos de largura cheia, e
 * sem isso o sublinhado atravessaria a gaveta inteira.
 */
function navLinkClass(active: boolean, size: string) {
  return cn(
    'inline-flex w-fit items-center gap-2 whitespace-nowrap border-b-2 py-1 font-semibold transition-colors',
    size,
    active
      ? 'border-primary text-primary'
      : 'border-transparent text-gray-600 hover:text-gray-900',
  )
}

/** Navegação da linha da marca. Some no mobile, onde quem serve é o hambúrguer. */
function DesktopNav({
  pathname,
  links,
}: {
  pathname: string
  links: NavLink[]
}) {
  return (
    <nav aria-label="Navegação principal" className="hidden shrink-0 md:block">
      <ul className="flex items-center gap-6">
        {links.map((link) => {
          const active = isActive(pathname, link)
          const Icon = link.icon

          return (
            <li key={link.href}>
              <a
                href={link.href}
                aria-current={active ? 'page' : undefined}
                className={navLinkClass(active, 'text-sm')}
              >
                <Icon className="size-4" aria-hidden="true" />
                {link.label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * O mesmo menu no mobile, numa gaveta.
 *
 * A gaveta só monta quando abre (padrão do Radix), então os links não existem em
 * duplicata no DOM enquanto ela está fechada.
 *
 * Não repete o landmark `<nav>`: dentro do diálogo ele seria um segundo
 * "Navegação principal" na página, e quem usa leitor de tela passaria a ver dois.
 */
function MobileNav({
  pathname,
  links,
}: {
  pathname: string
  links: NavLink[]
}) {
  const [open, setOpen] = useState(false)
  const onExplore = useOnExplore()

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Abrir menu"
          className="inline-flex size-10 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100 md:hidden"
        >
          <Menu className="size-5" aria-hidden="true" />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-72">
        <SheetHeader>
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>

        <ul className="flex flex-col gap-4 px-4">
          {links.map((link) => {
            const active = isActive(pathname, link)
            const Icon = link.icon

            return (
              <li key={link.href}>
                <a
                  href={link.href}
                  aria-current={active ? 'page' : undefined}
                  // Fecha na hora do clique: a navegação do Inertia troca a página
                  // sem desmontar o header, e a gaveta ficaria aberta por cima.
                  onClick={() => setOpen(false)}
                  className={navLinkClass(active, 'text-base')}
                >
                  <Icon className="size-5" aria-hidden="true" />
                  {link.label}
                </a>
              </li>
            )
          })}
        </ul>

        {/* O botão que não coube na linha do mobile. Largura cheia e no rodapé da
            gaveta: é a ação principal de quem abriu o menu sem ser cliente — e,
            como na linha do desktop, não é assunto do Explore. */}
        {!onExplore && (
          <WhatsAppLink
            href={WANT_BEWAVE.href}
            source="header_mobile"
            listingType="bewave"
            onClick={() => setOpen(false)}
            className="mx-4 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <MessageCircle className="size-4" aria-hidden="true" />
            {WANT_BEWAVE.label}
          </WhatsAppLink>
        )}
      </SheetContent>
    </Sheet>
  )
}

/**
 * Ação padrão do canto direito.
 *
 * Fica aqui, e não em cada página, porque este header é o mesmo em toda a área
 * pública — landing da plataforma, diretório e página da escola. Quem precisar de
 * outro conjunto passa `actions`; `actions={null}` remove todas.
 */
function DefaultActions({ hideExploreNav }: { hideExploreNav: boolean }) {
  const onExplore = useOnExplore()

  return (
    <>
      {/* Some no mobile, onde a linha não comporta os dois botões — lá ele está na
          gaveta do hambúrguer, inteiro. E some no Explore inteiro (ver
          WANT_BEWAVE). */}
      {!onExplore && (
        <WhatsAppLink
          href={WANT_BEWAVE.href}
          source="header"
          listingType="bewave"
          className="hidden items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 md:inline-flex"
        >
          <MessageCircle className="size-4" aria-hidden="true" />
          {WANT_BEWAVE.label}
        </WhatsAppLink>
      )}

      {/* O ícone de pessoa. Abre o menu com as duas contas da BeWave (admin e
          Explore) ou, para o criador logado, a própria conta. Na landing
          comercial (hideExploreNav) a porta do Explore some do menu — ver
          Home.tsx. */}
      <PublicUserMenu hideExplore={hideExploreNav} />
    </>
  )
}

interface PublicHeaderProps {
  /** `white` só quando o header estiver sobre fundo com cor. */
  logoVariant?: keyof typeof LOGO
  /** Para onde a marca leva. A landing não linka (você já está nela). */
  brandHref?: string
  /**
   * Cidades do select. Sem elas o select não é renderizado — é o que acontece numa
   * página que ainda não passe `cities` pelo controller.
   */
  cities?: CityOption[]
  selectedCity?: string | null
  /**
   * O Explore filtra ao vivo e passa o próprio handler. Sem ele, trocar de cidade
   * leva para /explore com o recorte escolhido — é o comportamento certo na
   * landing e na página da escola, onde não há lista para filtrar.
   */
  onCityChange?: (city: string) => void
  /** Ações no canto direito. `null` para não ter nenhuma. */
  actions?: ReactNode
  /**
   * Os links de páginas ("Início", "Explorar") — no desktop e na gaveta do
   * mobile. `false` nas telas do criador logado, onde o header é da CONTA dele:
   * ali a navegação do site é ruído, e o caminho de volta está no menu do
   * avatar.
   */
  showNav?: boolean
  /**
   * Linha extra, abaixo da linha da marca e dentro do mesmo bloco sticky. Quem
   * passa substitui a linha padrão (nenhuma) — é o que o diretório faz para
   * encaixar "Filtros" e a fila de selos.
   */
  belowRow?: ReactNode
  /** O diretório precisa vencer os panes do Leaflet (200-700) e os controles (800-1000). */
  className?: string
  /**
   * Tira "Explorar" da navegação e a porta de entrada do Explore do menu de
   * conta — só a landing comercial (Home) usa isto. O diretório e a página de
   * cada negócio continuam com a navegação completa: eles SÃO o Explore, não
   * fazem sentido escondendo o link para ele.
   */
  hideExploreNav?: boolean
}

export default function PublicHeader({
  logoVariant = 'default',
  brandHref,
  cities,
  selectedCity = null,
  onCityChange,
  hideExploreNav = false,
  actions = <DefaultActions hideExploreNav={hideExploreNav} />,
  showNav = true,
  belowRow,
  className,
}: PublicHeaderProps) {
  const ref = useRef<HTMLElement>(null)

  // `url` do Inertia é relativa e já vem com a query ("/explore?city=fortaleza").
  // Sai daqui uma vez só, e não dentro de cada navegação, para as duas acenderem o
  // mesmo link — a do desktop e a da gaveta do mobile.
  const { url, props } = usePage<{ creator_auth?: CreatorAuthProp }>()
  const onExplore = useOnExplore()
  const signedInCreator = Boolean(props.creator_auth?.creator)

  // `hideExploreNav` é o sinal de "estou na landing comercial" (ver Home.tsx).
  // "Minhas páginas" só no Explore: no apex o caminho seria para outro domínio,
  // e ali quem está logado é dono de escola, não criador de página.
  const navLinks = hideExploreNav
    ? [HOME_LINK]
    : [EXPLORE_LINK, ...(onExplore && signedInCreator ? [MY_PAGES_LINK] : [])]
  const pathname = url.split(/[?#]/)[0]

  // Publica a altura real do header numa custom property. O mapa e a folha da
  // lista ocupam "o que sobra da tela", e o header tem alturas diferentes quando
  // `belowRow` empilha uma segunda linha (hoje, só no diretório). Medir evita o
  // número mágico que desalinha toda vez que o header muda.
  useEffect(() => {
    const element = ref.current
    if (!element) return

    const publish = () =>
      document.documentElement.style.setProperty(
        '--public-header-h',
        `${element.offsetHeight}px`,
      )

    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Fora do Explore não há lista para filtrar: trocar de cidade é ir para o
  // Explore já com o recorte.
  const handleCityChange = useCallback(
    (city: string) =>
      onCityChange
        ? onCityChange(city)
        : router.get(EXPLORE_PATH, city === ALL_CITIES ? {} : { city }),
    [onCityChange],
  )

  // Largura automática: a logo é ~3,15:1, então travar a largura a deformaria.
  // Menor no mobile para sobrar espaço à navegação, que divide a linha com ela.
  // `alt` vazio porque o link ao lado já se identifica.
  const wordmark = (
    <img src={LOGO[logoVariant]} alt="" className="h-7 w-auto lg:h-8" />
  )

  return (
    <header
      ref={ref}
      className={cn(
        'sticky top-0 z-40 border-b border-gray-200 bg-white/85 backdrop-blur',
        className,
      )}
    >
      {/* Só a marca à esquerda; filtro de cidade, páginas e ações formam um
          bloco só, colado à direita. */}
      <div
        className={cn(
          PUBLIC_CONTAINER,
          'flex h-14 items-center justify-between gap-3 md:gap-4 lg:h-16',
        )}
      >
        <div className="flex shrink-0 items-center">
          {brandHref ? (
            <a href={brandHref} aria-label="BeWave">
              {wordmark}
            </a>
          ) : (
            wordmark
          )}
        </div>

        {/* `min-w-0` e NÃO `shrink-0`: com os dois lados travados, a linha não
            tinha como caber num celular e transbordava — medido em 390px, ela
            pedia 402 (marca 88 + gutter + o select de cidade em 186 + as ações
            em 84), e esses 12px de sobra deixavam TODA página pública rolar
            para a direita. Quem cede é este lado, porque o select de cidade é o
            único item com texto de largura variável; a marca à esquerda tem
            tamanho fixo e encolher uma logo é pior do que truncar um rótulo. */}
        <div className="flex min-w-0 items-center gap-3 md:gap-4">
          {cities && (
            <CitySelect
              cities={cities}
              selected={selectedCity}
              onChange={handleCityChange}
              // O rótulo trunca (o SelectTrigger já traz `line-clamp-1` no
              // valor) em vez de empurrar a linha.
              className="min-w-0 rounded-full border bg-white shadow-sm"
            />
          )}

          {showNav && <DesktopNav pathname={pathname} links={navLinks} />}

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {actions}
            {showNav && <MobileNav pathname={pathname} links={navLinks} />}
          </div>
        </div>
      </div>

      {belowRow && (
        <div
          className={cn(
            PUBLIC_CONTAINER,
            'space-y-2 border-t border-gray-100 py-3',
          )}
        >
          {belowRow}
        </div>
      )}
    </header>
  )
}
